// Notification sounds using Web Audio API

let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
};

/**
 * Play a pleasant chime sound for new items in search filters
 * Two-tone ascending chime
 */
export const playFilterNotification = (): void => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // First tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.frequency.value = 587.33; // D5
    osc1.type = 'sine';
    gain1.gain.setValueAtTime(0.3, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
    osc1.start(now);
    osc1.stop(now + 0.3);

    // Second tone (higher)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 880; // A5
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0, now + 0.1);
    gain2.gain.linearRampToValueAtTime(0.25, now + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.5);
  } catch (e) {
    console.warn('Could not play filter notification sound:', e);
  }
};

/**
 * Play a soft notification sound for incoming chat messages
 * Gentle, quiet "pop" sound
 */
export const playMessageNotification = (): void => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Soft pop sound
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.frequency.value = 440; // A4 - softer frequency
    osc.type = 'sine';

    // Very soft and short
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    osc.start(now);
    osc.stop(now + 0.15);
  } catch (e) {
    console.warn('Could not play message notification sound:', e);
  }
};

/**
 * Play a quick "tick" sound when sending a message
 * Fast, snappy confirmation
 */
export const playMessageSentSound = (): void => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Quick tick/click sound
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sine';
    osc.frequency.value = 1200; // High pitch for snappy feel

    // Very fast attack and decay
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

    osc.start(now);
    osc.stop(now + 0.05);
  } catch (e) {
    console.warn('Could not play message sent sound:', e);
  }
};

/**
 * Play a distinctive sound for system messages (deal acceptance/confirmation/rejection)
 * Three-tone notification
 */
export const playSystemNotification = (): void => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Three ascending tones for importance
    const frequencies = [440, 554.37, 659.25]; // A4, C#5, E5 (A major chord)
    const durations = [0.15, 0.15, 0.3];
    let startTime = now;

    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.value = freq;
      osc.type = 'sine';

      const duration = durations[i];
      gain.gain.setValueAtTime(0.25, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

      osc.start(startTime);
      osc.stop(startTime + duration);

      startTime += duration * 0.7; // Slight overlap for smoother sound
    });
  } catch (e) {
    console.warn('Could not play system notification sound:', e);
  }
};

/**
 * Initialize audio context on user interaction (required by browsers)
 */
export const initAudioContext = (): void => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
  } catch (e) {
    console.warn('Could not initialize audio context:', e);
  }
};
